import * as Collapsible from "@radix-ui/react-collapsible";
import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from "@radix-ui/react-icons";
import { Badge, Button, Callout, Card, Flex, Text } from "@radix-ui/themes";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Booking, formatCurrencyFromCents } from "use-smart-locks-shared";
import { routes } from "../../routes";
import { getApiUserErrorMessage } from "../../shared/api/nest-api";
import { FullSpinner } from "../../shared/components/full-spinner";
import { useDeviceIdQuery } from "../../shared/queries/use-device-id-query";
import { useMyBookingsQuery } from "../queries/use-my-bookings-query";
import { OpenBookingByNumberForm } from "./open-booking-by-number-form.component";

type BookingStatus = "active" | "upcoming" | "ended";

function getBookingStatus(booking: Booking): BookingStatus {
  if (booking.endedAt) {
    return "ended";
  }

  const now = new Date();
  const ends = new Date(booking.ends);

  if (ends < now) {
    return "ended";
  }
  if (booking.startedAt) {
    return "active";
  }
  return "upcoming";
}

function isActiveOrUpcoming(booking: Booking): boolean {
  return getBookingStatus(booking) !== "ended";
}

const statusOrder: Record<BookingStatus, number> = {
  active: 0,
  upcoming: 1,
  ended: 2,
};

function sortBookingsByStatus(bookings: Booking[]): Booking[] {
  return [...bookings].sort(
    (a, b) =>
      statusOrder[getBookingStatus(a)] - statusOrder[getBookingStatus(b)],
  );
}

function getStatusBadge(status: BookingStatus) {
  switch (status) {
    case "active":
      return { color: "green" as const, label: "Aktiv" };
    case "upcoming":
      return { color: "blue" as const, label: "Bevorstehend" };
    case "ended":
      return { color: "gray" as const, label: "Beendet" };
  }
}

function getFormattedTotalCost(
  booking: Booking,
  status: BookingStatus,
): string | null {
  if (status !== "ended") {
    return null;
  }

  return formatCurrencyFromCents(booking.totalAmountPaidInCents ?? 0);
}

function BookingCard({
  booking,
  showOpenButton,
}: {
  booking: Booking;
  showOpenButton: boolean;
}) {
  const status = getBookingStatus(booking);
  const badge = getStatusBadge(status);
  const formattedTotalCost = getFormattedTotalCost(booking, status);

  return (
    <Card size="2">
      <Flex direction="column" gap="8px">
        <Flex justify="between" align="center">
          <Text size="2" weight="medium">
            {booking.stationName}
          </Text>
          <Badge color={badge.color} variant="soft">
            {badge.label}
          </Badge>
        </Flex>

        <Text size="1" color="gray">
          {new Date(booking.starts).toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}{" "}
          -{" "}
          {new Date(booking.ends).toLocaleTimeString("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Text>

        {formattedTotalCost && (
          <Flex justify="between" align="center">
            <Text size="1" color="gray">
              Gesamtpreis
            </Text>
            <Text size="1" weight="medium">
              {formattedTotalCost}
            </Text>
          </Flex>
        )}

        <Flex justify="between" align="center">
          <Text size="1" color="gray">
            {booking.units.map((u) => u.name).join(", ")}
          </Text>
          {showOpenButton && (
            <Button size="2" variant="soft" asChild>
              <NavLink to={routes.bookings.detail(booking.slug)}>
                Buchung öffnen
              </NavLink>
            </Button>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}

function PastBookingsSection({ bookings }: { bookings: Booking[] }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
      <Flex direction="column" gap="16px">
        <Collapsible.Trigger asChild>
          <Flex align="center" gap="8px" style={{ cursor: "pointer" }}>
            <Text weight="bold">Vergangene Buchungen</Text>
            <Badge color="gray" variant="soft" size="1">
              {bookings.length}
            </Badge>
            <ChevronDownIcon
              style={{
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 150ms ease",
              }}
            />
          </Flex>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Flex direction="column" gap="16px">
            {bookings.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                showOpenButton={false}
              />
            ))}
          </Flex>
        </Collapsible.Content>
      </Flex>
    </Collapsible.Root>
  );
}

export function MyBookingsList() {
  const { data: deviceId } = useDeviceIdQuery();
  const {
    data: bookings,
    isLoading,
    isError,
    error,
  } = useMyBookingsQuery(deviceId);

  const sortedBookings = bookings ? sortBookingsByStatus(bookings) : undefined;
  const activeOrUpcomingBookings =
    sortedBookings?.filter(isActiveOrUpcoming) ?? [];
  const pastBookings =
    sortedBookings?.filter((b) => !isActiveOrUpcoming(b)) ?? [];

  return (
    <Flex direction="column" gap="48px" width="100%">
      {isLoading ? (
        <FullSpinner />
      ) : isError ? (
        <Callout.Root color="red">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>
            {getApiUserErrorMessage(error) ??
              "Leider ist ein Fehler beim Laden der Buchungen aufgetreten."}
          </Callout.Text>
        </Callout.Root>
      ) : (
        <>
          <Flex direction="column" gap="32px">
            <Flex direction="column" gap="16px">
              <Flex align="center" gap="8px">
                <Text weight="bold">Aktive oder bevorstehende Buchungen</Text>
                <Badge color="gray" variant="soft" size="1">
                  {activeOrUpcomingBookings.length}
                </Badge>
              </Flex>
              {activeOrUpcomingBookings.length > 0 ? (
                activeOrUpcomingBookings.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    showOpenButton={true}
                  />
                ))
              ) : (
                <Callout.Root color="blue">
                  <Callout.Icon>
                    <ExclamationTriangleIcon />
                  </Callout.Icon>
                  <Callout.Text>
                    Keine aktiven oder bevorstehenden Buchungen
                  </Callout.Text>
                </Callout.Root>
              )}
            </Flex>
            <OpenBookingByNumberForm />
          </Flex>

          {pastBookings.length > 0 && (
            <PastBookingsSection bookings={pastBookings} />
          )}
        </>
      )}
    </Flex>
  );
}
